/*
  What each thing the app records is called, in plain words.

  **Three of the eighteen events in OnHand Testing used to print as
  `SITE_CREATED`, `SITE_DEACTIVATED` and `SITE_SETTINGS_UPDATED`**, in capitals
  with underscores, on the screen, because this list had no entry for them.
  Thirteen of the actions the app writes had no words at all. Group F found it
  and Group F's drawings print sentences.

  **They are sentences now rather than headings.** "Signed in", not "Logged In".
  The screen is a list of things that happened, so each row reads as one.

  Anything that calls `logAudit` gets its words here in the same sitting, or a
  person reads the name of a variable.
*/
export const ACTION_WORDS = {
  USER_LOGIN:                'Signed in',
  USER_LOGOUT:               'Signed out',
  SESSION_TIMEOUT:           'Session timed out',
  IMPORT_ACCOUNTS:           'Imported accounts',
  IMPORT_USERS:              'Imported people',
  IMPORT_CLAIMSOFT_CATALOG:  'Imported the Claimsoft catalog',
  IMPORT_EDGE_CATALOG:       'Imported the Account Edge catalog',
  IMPORT_REFERRAL_SOURCES:   'Imported referral sources',
  EXPORT_ACCOUNTS:           'Exported accounts',
  EXPORT_USERS:              'Exported the people list',
  EXPORT_CATALOG:            'Exported the item catalog',
  EXPORT_COUNT_HISTORY:      'Exported count history',
  EXPORT_LOCATIONS:          'Exported the locations list',
  EXPORT_NON_INVENTORY:      'Exported non-inventory items',
  EXPORT_DEAD_STOCK:         'Exported dead stock items',
  USER_CREATED:              'Added a person',
  USER_UPDATED:              'Changed a person',
  USER_DEACTIVATED:          'Switched a person off',
  USER_ACTIVATED:            'Switched a person on',
  USER_PASSWORD_RESET:       'Reset a password',
  ACCOUNT_REP_ADDED:         'Gave a rep a location',
  ACCOUNT_REP_REMOVED:       'Took a location off a rep',
  ACCOUNT_CATALOG_CHANGED:   'Changed a location catalog',
  ACCOUNT_DEACTIVATED:       'Switched a location off',
  ACCOUNT_ACTIVATED:         'Switched a location on',
  ACCOUNT_CLOSURE_FLAGGED:   'Flagged a location as closing',
  ACCOUNT_CLOSURE_APPROVED:  'Agreed a location has closed',
  ACCOUNT_REACTIVATED:       'Reopened a location',
  COUNT_SUBMITTED:           'Handed in a count sheet',
  COUNT_APPROVED:            'Approved a count sheet',
  COUNT_REJECTED:            'Sent a count sheet back',
  COUNT_EDIT_REQUESTED:      'Asked to change a count sheet',
  COUNT_EDIT_APPROVED:       'Allowed a count sheet to be changed',
  COUNT_EDIT_DENIED:         'Refused a change to a count sheet',
  CATALOG_ITEM_ADDED:        'Added a catalog item',
  CATALOG_ITEM_UPDATED:      'Changed a catalog item',
  SITE_CREATED:              'Created a site',
  SITE_ACTIVATED:            'Switched a site on',
  SITE_DEACTIVATED:          'Switched a site off',
  SITE_SETTINGS_UPDATED:     'Changed site settings',
  WAREHOUSE_CREATED:         'Added a location',
  WAREHOUSE_UPDATED:         'Changed a location',
  WAREHOUSE_DELETED:         'Deleted a location',
  WAREHOUSES_IMPORTED:       'Imported locations',
  GLOBAL_ADMIN_GRANTED:      'Made somebody a Global Admin',
  GLOBAL_ADMIN_REVOKED:      'Took Global Admin away',
  // Job 17, 7 September 2026. Written in the same sitting as the two things
  // that record them, because the rule above is that anything calling for an
  // audit line gets its words here or a person reads the name of a variable.
  EXPORT_EVERYTHING:         'Took a full copy of the site',
  RETENTION_RUN:             'Removed data past its keeping time',
};

/*
  A row with no words is the fault Group F found, so it is worth being able to
  see one rather than only hoping there are none. This is what a row falls back
  to, and it is deliberately readable: `SITE_SETTINGS_UPDATED` becomes "Site
  settings updated" rather than being printed raw.
*/
export function actionWords(action) {
  if (!action) return 'Something happened';
  if (ACTION_WORDS[action]) return ACTION_WORDS[action];
  const words = action.toLowerCase().replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/*
  The kinds of thing the log records.

  **The filter used to offer three that can never match and hide two that are
  written**, which is the same hole from both sides. `account`, `count` and
  `catalog` were offered and nothing wrote them; `site` and `warehouse` were
  written every time somebody changed a site or a location and could not be
  chosen. Every kind is offered now, and **each one says how many events it
  holds**, so a kind that can never match says 0 rather than looking like a
  filter that found nothing.
*/
export const KINDS = [
  { key: 'auth',      label: 'Signing in' },
  { key: 'count',     label: 'Counting' },
  { key: 'user',      label: 'People and access' },
  { key: 'warehouse', label: 'Locations' },
  { key: 'site',      label: 'Sites' },
  { key: 'catalog',   label: 'The item catalog' },
  { key: 'import',    label: 'Imports' },
  { key: 'export',    label: 'Exports' },
  { key: 'data',      label: 'Copies and removals' },
];

export function kindLabel(key) {
  return KINDS.find(k => k.key === key)?.label || key || 'Other';
}

/*
  The strip of numbers above the rows. **The red 0 beside Counting is the
  finding, on the face of the screen**: nothing about counting has ever been
  recorded, so the one screen whose job is to answer "who approved this count,
  and when" has never been given the events.
*/
export function auditFigures(rows) {
  const people = new Set();
  let auth = 0, sites = 0, counting = 0;
  for (const r of rows) {
    if (r.user_id) people.add(r.user_id);
    if (r.category === 'auth')  auth += 1;
    if (r.category === 'site')  sites += 1;
    if (r.category === 'count') counting += 1;
  }
  return { events: rows.length, people: people.size, auth, sites, counting };
}

export function kindCounts(rows) {
  const out = {};
  for (const k of KINDS) out[k.key] = 0;
  for (const r of rows) if (out[r.category] !== undefined) out[r.category] += 1;
  return out;
}
